import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  IMPORT_FIELD_DEFS,
  IMPORT_TYPES,
  applyColumnMapping,
  errorsToCsv,
  type ImportType
} from '@finvox/shared/import';
import { Button } from '@/components/ui/Button';
import { textLinkVariants } from '@/components/ui/button-variants';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useOrg } from '@/contexts/OrgContext';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { parseSpreadsheetFile } from '@/lib/parse-spreadsheet';
import { downloadImportSample } from '@/lib/import-sample-csv';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { ConnectorMarketplace } from '@/components/ConnectorMarketplace';

type Step = 'source' | 'map' | 'validate' | 'done';

type MappingProfile = {
  id: string;
  name: string;
  importType: ImportType;
  mapping: Record<string, string>;
};

type DryRunResult = {
  ok: boolean;
  totalRows: number;
  validCount: number;
  errorCount: number;
  errors: { rowIndex: number; field?: string; message: string }[];
};

type CommitResult = {
  jobId: string;
  cursor: number;
  done: boolean;
  processed: number;
  skipped: number;
  failed: number;
};

const STEPS: Step[] = ['source', 'map', 'validate', 'done'];

function guessMapping(headers: string[], importType: ImportType): Record<string, string> {
  const fields = IMPORT_FIELD_DEFS[importType];
  const mapping: Record<string, string> = {};
  const lower = new Map(headers.map((h) => [h.toLowerCase().replace(/\s+/g, ''), h]));
  for (const f of fields) {
    const candidates = [f.key, f.key.replace(/([A-Z])/g, ' $1'), f.key.replace(/_/g, ' ')];
    for (const c of candidates) {
      const hit = lower.get(c.toLowerCase());
      if (hit) {
        mapping[f.key] = hit;
        break;
      }
    }
  }
  return mapping;
}

export function ImportPage() {
  const { t } = useTranslation();
  const { orgId, currentPeriodId } = useOrg();
  const online = useOnlineStatus();

  const [step, setStep] = useState<Step>('source');
  const [importType, setImportType] = useState<ImportType>('coa');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [profileName, setProfileName] = useState('');
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [commitStats, setCommitStats] = useState<{
    processed: number;
    skipped: number;
    failed: number;
    jobId: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  type ImportJob = {
    id: string;
    importType: string;
    status: string;
    processed: number;
    skipped: number;
    failed: number;
    totalRows: number;
  };

  const mappedRows = useMemo(
    () => applyColumnMapping(headers, rawRows, mapping),
    [headers, rawRows, mapping]
  );

  const fieldDefs = IMPORT_FIELD_DEFS[importType];

  const mapProfile = useCallback(
    (id: string, data: Record<string, unknown>) =>
      ({ id, ...(data as Omit<MappingProfile, 'id'>) }) as MappingProfile,
    []
  );

  const profilesPage = useFirestorePageQuery<MappingProfile>({
    orgId,
    collectionPath: 'importMappingProfiles',
    orderByField: 'createdAt',
    orderDirection: 'desc',
    mapDoc: mapProfile
  });

  const profiles = profilesPage.rows;

  const jobsPage = useFirestorePageQuery<ImportJob>({
    orgId,
    collectionPath: 'importJobs',
    orderByField: 'createdAt',
    orderDirection: 'desc',
    mapDoc: (id, data) => ({
      id,
      importType: String(data.importType ?? ''),
      status: String(data.status ?? ''),
      processed: Number(data.processed ?? 0),
      skipped: Number(data.skipped ?? 0),
      failed: Number(data.failed ?? 0),
      totalRows: Number(data.totalRows ?? 0)
    })
  });

  async function onFile(file: File) {
    try {
      const parsed = await parseSpreadsheetFile(file);
      setHeaders(parsed.headers);
      setRawRows(parsed.rows);
      setMapping(guessMapping(parsed.headers, importType));
      setDryRun(null);
      setCommitStats(null);
    } catch (e) {
      toastError(toast, e, 'import', 'import.errors.parseFailed');
    }
  }

  const loadProfile = useCallback(
    (profileId: string) => {
      const p = profiles.find((x) => x.id === profileId);
      if (!p) return;
      setImportType(p.importType);
      setMapping(p.mapping);
    },
    [profiles]
  );

  async function saveProfile() {
    if (!orgId || !online || !profileName.trim()) return;
    const db = getFirestoreDb();
    const ref = doc(collection(db, 'organizations', orgId, 'importMappingProfiles'));
    await setDoc(ref, {
      name: profileName.trim(),
      importType,
      mapping,
      createdAt: serverTimestamp()
    });
    setProfileName('');
    profilesPage.refresh();
  }

  async function runDryRun() {
    if (!online) return;
    setBusy(true);
    try {
      const result = await apiPost<DryRunResult>('/v1/imports/dry-run', {
        importType,
        rows: mappedRows
      });
      setDryRun(result);
      if (result.ok) setStep('validate');
    } catch (e) {
      toastError(toast, e, 'import', 'import.errors.validationFailed');
    } finally {
      setBusy(false);
    }
  }

  async function runCommit() {
    if (!orgId || !online) return;
    if (importType === 'opening_balances' && !currentPeriodId) {
      toast.error(t('import.periodRequired'), 'import');
      return;
    }
    setBusy(true);
    let cursor = 0;
    let jobId: string | undefined;
    let totalProcessed = 0;
    let totalSkipped = 0;
    let totalFailed = 0;

    try {
      for (;;) {
        const res = await apiPost<CommitResult>('/v1/imports/commit', {
          orgId,
          importType,
          rows: mappedRows,
          cursor,
          jobId,
          periodId: importType === 'opening_balances' ? currentPeriodId : undefined
        });
        jobId = res.jobId;
        cursor = res.cursor;
        totalProcessed += res.processed;
        totalSkipped += res.skipped;
        totalFailed += res.failed;
        if (res.done) {
          setCommitStats({
            processed: totalProcessed,
            skipped: totalSkipped,
            failed: totalFailed,
            jobId: res.jobId
          });
          setStep('done');
          break;
        }
      }
    } catch (e) {
      toastError(toast, e, 'import', 'import.errors.importFailed');
    } finally {
      setBusy(false);
    }
  }

  function downloadErrors() {
    if (!dryRun?.errors.length) return;
    const csv = errorsToCsv(dryRun.errors);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function reset() {
    setStep('source');
    setHeaders([]);
    setRawRows([]);
    setMapping({});
    setDryRun(null);
    setCommitStats(null);
  }

  const stepIndex = STEPS.indexOf(step);

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('nav.importIntegrations')}</h1>
      <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('import.subtitle')}</p>

      <div className="mt-4">
        <ConnectorMarketplace />
      </div>

      <div className="mt-8">
        <ol className="flex flex-wrap gap-2 text-sm">
          {STEPS.map((s, i) => (
            <li
              key={s}
              className={
                i <= stepIndex
                  ? 'rounded-full bg-[var(--fv-primary)] px-3 py-1 text-white'
                  : 'rounded-full border border-[var(--fv-border)] px-3 py-1 text-[var(--fv-text-muted)]'
              }
            >
              {t(`import.steps.${s}`)}
            </li>
          ))}
        </ol>
      </div>

      {step === 'source' && (
        <div className="mt-6 space-y-4">
          <label className="flex flex-col gap-1 text-sm">
            {t('import.importType')}
            <Select
              className="max-w-md"
              value={importType}
              onValueChange={(v) => setImportType(v as ImportType)}
              options={IMPORT_TYPES.map((type) => ({
                value: type,
                label: t(`import.types.${type}`)
              }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            {t('import.uploadFile')}
            <input
              type="file"
              accept=".csv,.xlsx,.xls,text/csv"
              className="max-w-md"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFile(f);
              }}
            />
          </label>
          <Button variant="secondary" onClick={() => downloadImportSample(importType)}>
            {t('import.downloadSample')}
          </Button>
          {rawRows.length > 0 && (
            <p className="text-sm text-[var(--fv-text-muted)]">
              {t('import.rowsLoaded', { count: rawRows.length })}
            </p>
          )}
          <Button disabled={!rawRows.length} onClick={() => setStep('map')}>
            {t('import.next')}
          </Button>
        </div>
      )}

      {step === 'map' && (
        <div className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Select
              value=""
              placeholder={t('import.loadProfile')}
              onValueChange={(id) => loadProfile(id)}
              options={profiles
                .filter((p) => p.importType === importType)
                .map((p) => ({ value: p.id, label: p.name }))}
            />
            <Input
              className="max-w-xs"
              placeholder={t('import.profileName')}
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
            />
            <Button variant="secondary" onClick={() => void saveProfile()} disabled={!online}>
              {t('import.saveProfile')}
            </Button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-[var(--fv-border)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--fv-bg)]">
                <tr>
                  <th className="p-2 text-start">{t('import.finVoxField')}</th>
                  <th className="p-2 text-start">{t('import.fileColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {fieldDefs.map((f) => (
                  <tr key={f.key} className="border-t border-[var(--fv-border)]">
                    <td className="p-2">
                      {t(f.labelKey)}
                      {f.required && <span className="text-red-600"> *</span>}
                    </td>
                    <td className="p-2">
                      <Select
                        className="w-full"
                        value={mapping[f.key] ?? ''}
                        placeholder="—"
                        onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v }))}
                        options={[
                          { value: '', label: '—' },
                          ...headers.map((h) => ({ value: h, label: h }))
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setStep('source')}>
              {t('import.back')}
            </Button>
            <Button onClick={() => void runDryRun()} disabled={busy || !online}>
              {busy ? t('import.validating') : t('import.validate')}
            </Button>
          </div>
        </div>
      )}

      {step === 'validate' && dryRun && (
        <div className="mt-6 space-y-4">
          {dryRun.ok ? (
            <p className="text-sm text-green-700">{t('import.validationOk', { count: dryRun.validCount })}</p>
          ) : (
            <>
              <p className="text-sm text-red-600">
                {t('import.validationFailed', { count: dryRun.errorCount })}
              </p>
              <Button variant="secondary" onClick={downloadErrors}>
                {t('import.downloadErrors')}
              </Button>
            </>
          )}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setStep('map')}>
              {t('import.back')}
            </Button>
            <Button onClick={() => void runCommit()} disabled={!dryRun.ok || busy || !online}>
              {busy ? t('import.importing') : t('import.commit')}
            </Button>
          </div>
        </div>
      )}

      {step === 'done' && commitStats && (
        <div className="mt-6 space-y-4 rounded-lg border border-[var(--fv-border)] p-4">
          <h2 className="text-lg font-semibold">{t('import.checklistTitle')}</h2>
          <ul className="list-inside list-disc space-y-2 text-sm">
            <li>
              {t('import.statsProcessed', { n: commitStats.processed })}
            </li>
            <li>{t('import.statsSkipped', { n: commitStats.skipped })}</li>
            {commitStats.failed > 0 && (
              <li className="text-red-600">
                {t('import.statsFailed', { n: commitStats.failed })}
              </li>
            )}
            <li>
              <Link to="/coa" className={textLinkVariants()}>
                {t('import.checkCoa')}
              </Link>
            </li>
            <li>
              <Link to="/journals" className={textLinkVariants()}>
                {t('import.checkJournals')}
              </Link>
            </li>
            <li>
              <Link to="/reports" className={textLinkVariants()}>
                {t('import.checkReports')}
              </Link>
            </li>
          </ul>
          <Button onClick={reset}>{t('import.newImport')}</Button>
        </div>
      )}

      {(jobsPage.rows.length > 0 || jobsPage.loading) && (
        <section className="mt-10">
          <h2 className="text-sm font-semibold">{t('import.recentJobs')}</h2>
          <PaginatedTable
            className="mt-2"
            loading={jobsPage.loading}
            pageIndex={jobsPage.pageIndex}
            hasNext={jobsPage.hasNext}
            hasPrev={jobsPage.hasPrev}
            onNext={jobsPage.nextPage}
            onPrev={jobsPage.prevPage}
            pageSize={jobsPage.pageSize}
            onPageSizeChange={jobsPage.setPageSize}
          >
            <ul className="divide-y divide-[var(--fv-border)] p-2 text-sm">
              {jobsPage.rows.map((j) => (
                <li key={j.id} className="py-2">
                  {t(`import.types.${j.importType as ImportType}`, { defaultValue: j.importType })} —{' '}
                  {j.status} — {j.processed}/{j.totalRows} ({t('import.statsSkipped', { n: j.skipped })})
                  {j.failed > 0 && ` — ${t('import.statsFailed', { n: j.failed })}`}
                </li>
              ))}
            </ul>
          </PaginatedTable>
        </section>
      )}

    </div>
  );
}
